import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Upload, FileText, AlertCircle, CheckCircle } from 'lucide-react';
import regressionService from './api';
import ResultsTable from './components/ResultsTable';
import MetricsDisplay from './components/MetricsDisplay';

/**
 * Guardrail Regression Analysis component
 * @returns {React.ReactElement} - The regression analysis component
 */
const Regression = () => {
    const navigate = useNavigate();
    const [models, setModels] = useState([]);
    const [selectedModel, setSelectedModel] = useState('');
    const [file, setFile] = useState(null);
    const [fileName, setFileName] = useState('No file selected');
    const [isUploading, setIsUploading] = useState(false);
    const [uploadStatus, setUploadStatus] = useState('');
    const [error, setError] = useState('');
    const [results, setResults] = useState(null);
    const [metrics, setMetrics] = useState([]);

    useEffect(() => {
        regressionService.getModels()
            .then((list) => {
                setModels(list);
                const preferred = 'gpt-4o-mini';
                setSelectedModel(list.includes(preferred) ? preferred : list[0] ?? '');
            })
            .catch(() => {});
    }, []);

    const handleBackToSettings = () => {
        navigate('/settings');
    };

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            if (selectedFile.type !== 'text/csv' && !selectedFile.name.endsWith('.csv')) {
                setError('Please select a valid CSV file');
                setFile(null);
                setFileName('No file selected');
                return;
            }
            setFile(selectedFile);
            setFileName(selectedFile.name);
            setError('');
        } else {
            setFile(null);
            setFileName('No file selected');
        }
    };

    const handleUploadClick = () => {
        document.getElementById('file-upload').click();
    };

    const handleAnalyze = async () => {
        if (!file) {
            setError('Please select a CSV file first');
            return;
        }

        setIsUploading(true);
        setUploadStatus('Analyzing data... This may take a moment.');
        setError('');

        try {
            const result = await regressionService.evaluateRegression(file, selectedModel);

            if (result.status === 'success') {
                setResults(result.results || []);
                setMetrics(result.metrics || []);
                setUploadStatus('Analysis Complete');
            } else {
                throw new Error('Analysis failed');
            }
        } catch (error) {
            setUploadStatus('');
            setError(`Error: ${error.message || 'Failed to analyze regression data'}`);
        } finally {
            setIsUploading(false);
        }
    };

    const getCompositeScore = () => {
        const compositeMetric = metrics.find(metric => metric.Metric === 'Composite Score');
        return compositeMetric ? compositeMetric.Score : null;
    };

    return (
        <div className="bg-gray-50 min-h-full">
            <div className="py-4 mb-5">
                {models.length > 0 && (
                    <div className="flex justify-end mb-4">
                        <div className="flex items-center gap-2">
                            <label className="text-xs font-medium text-primary whitespace-nowrap">Model</label>
                            <select
                                value={selectedModel}
                                onChange={(e) => setSelectedModel(e.target.value)}
                                className="border border-primary text-primary text-xs rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary bg-white"
                            >
                                {models.map((model) => (
                                    <option key={model} value={model}>{model}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                )}

                <div className="mb-3">
          <h2 className="text-[14px] font-semibold text-gray-600 uppercase tracking-[0.7px]">Model Evaluation</h2>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200 mb-6">

                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                        <input
                            type="file"
                            id="file-upload"
                            accept=".csv"
                            onChange={handleFileChange}
                            className="hidden"
                        />

                        <div className="flex justify-center mb-3">
                            <FileText size={32} className="text-gray-400" />
                        </div>

                        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                            <button
                                onClick={handleUploadClick}
                                className="h-10 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-200 flex items-center gap-2"
                            >
                                <Upload size={16} />
                                Select File
                            </button>

                            <button
                                onClick={handleAnalyze}
                                disabled={!file || isUploading}
                                className={`h-10 px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 relative overflow-hidden ${!file || isUploading
                                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                    : 'bg-primary text-white hover:bg-primaryDark'
                                    }`}
                            >
                                {isUploading ? (
                                    <>
                                        <style>{`
                                            @keyframes sparkle {
                                                0%, 100% { opacity: 0; transform: scale(0) rotate(0deg); }
                                                50% { opacity: 1; transform: scale(1) rotate(180deg); }
                                            }
                                            @keyframes shimmer {
                                                0% { background-position: -200% center; }
                                                100% { background-position: 200% center; }
                                            }
                                            .ai-shimmer {
                                                background: linear-gradient(90deg, #6366f1, #a855f7, #ec4899, #a855f7, #6366f1);
                                                background-size: 200% auto;
                                                animation: shimmer 2s linear infinite;
                                                -webkit-background-clip: text;
                                                -webkit-text-fill-color: transparent;
                                                background-clip: text;
                                            }
                                            .sparkle-dot {
                                                animation: sparkle 1.4s ease-in-out infinite;
                                            }
                                            .sparkle-dot:nth-child(2) { animation-delay: 0.2s; }
                                            .sparkle-dot:nth-child(3) { animation-delay: 0.4s; }
                                        `}</style>
                                        <span className="flex items-center gap-0.5">
                                            <span className="sparkle-dot text-purple-400">✦</span>
                                            <span className="sparkle-dot text-pink-400">✦</span>
                                            <span className="sparkle-dot text-indigo-400">✦</span>
                                        </span>
                                        <span className="ai-shimmer font-medium">Analyzing...</span>
                                    </>
                                ) : (
                                    <>Analyze</>
                                )}
                            </button>
                        </div>

                        <div className="mt-4 text-sm">
                            <span className="font-medium">Selected file:</span> {fileName}
                        </div>
                    </div>

                    {error && (
                        <div className="mt-4 p-3 bg-red-50 text-red-700 rounded-md flex items-start gap-2">
                            <AlertCircle size={18} className="mt-0.5 flex-shrink-0" />
                            <span>{error}</span>
                        </div>
                    )}

                    {uploadStatus && !error && (
                        <div className="mt-4 p-3 bg-blue-50 text-primaryDark rounded-md flex items-start gap-2">
                            {uploadStatus === 'Analysis Complete' ? (
                                <CheckCircle size={18} className="mt-0.5 flex-shrink-0" />
                            ) : (
                                <div className="w-4 h-4 mt-0.5 border-2 border-t-transparent border-blue-700 rounded-full animate-spin"></div>
                            )}
                            <span>{uploadStatus}</span>
                        </div>
                    )}
                </div>

                {results && results.length > 0 && (
                    <>
                        <ResultsTable results={results} />

                        {metrics.length > 0 && <MetricsDisplay metrics={metrics} />}
                    </>
                )}
            </div>
        </div>
    );
};

export default Regression;